import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { Copy, Download, Plus } from 'lucide-react';
import { exportCouponsCsv } from '../api/adminCampaigns.api';
import { useCoupons, useCreateCoupons, usePatchCoupon } from '../hooks/useCampaigns';
import { errorText, formatDateTime } from '../format';
import { useNotification } from '../../../../hooks/useNotification';
import type { Coupon } from '../types';

const CreateCouponDialog: React.FC<{ campaignId: string; open: boolean; onClose: () => void }> = ({
  campaignId,
  open,
  onClose,
}) => {
  const notification = useNotification();
  const create = useCreateCoupons();
  const [mode, setMode] = useState<'manual' | 'bulk'>('manual');
  const [code, setCode] = useState('');
  const [prefix, setPrefix] = useState('');
  const [count, setCount] = useState('100');
  const [maxUsage, setMaxUsage] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    setError(null);
    const base = { maxUsage: maxUsage ? Number(maxUsage) : mode === 'bulk' ? 1 : null };
    const input =
      mode === 'manual'
        ? { ...base, code: code.trim() }
        : { ...base, prefix: prefix.trim(), count: Number(count) };
    create.mutate(
      { publicId: campaignId, input },
      {
        onSuccess: (rows) => {
          notification.success(`${rows.length} coupon${rows.length === 1 ? '' : 's'} created`);
          setCode('');
          onClose();
        },
        onError: (err) => setError(errorText(err)),
      }
    );
  };

  const ready =
    mode === 'manual'
      ? code.trim().length >= 4
      : prefix.trim().length > 0 && Number(count) >= 1 && Number(count) <= 5000;

  return (
    <Dialog open={open} onClose={create.isPending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add coupons</DialogTitle>
      <DialogContent>
        <div className="mt-1 space-y-3">
          <Tabs value={mode} onChange={(_, value) => setMode(value)}>
            <Tab value="manual" label="Single code" />
            <Tab value="bulk" label="Generate in bulk" />
          </Tabs>
          {error && <Alert severity="error">{error}</Alert>}
          {mode === 'manual' ? (
            <TextField
              label="Code"
              size="small"
              fullWidth
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              helperText="4–32 characters: A–Z, 0–9, hyphen. Leave usage empty for a public, unlimited code."
            />
          ) : (
            <>
              <TextField
                label="Prefix"
                size="small"
                fullWidth
                value={prefix}
                onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                helperText="e.g. SPRING → SPRING-7K3QX92M4T"
              />
              <TextField
                label="How many (1–5000)"
                size="small"
                fullWidth
                value={count}
                onChange={(e) => setCount(e.target.value.replace(/\D/g, ''))}
              />
            </>
          )}
          <TextField
            label="Max uses per code"
            size="small"
            fullWidth
            value={maxUsage}
            onChange={(e) => setMaxUsage(e.target.value.replace(/\D/g, ''))}
            helperText={mode === 'bulk' ? 'Default 1 (single-use codes)' : 'Empty = unlimited'}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={create.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={!ready || create.isPending}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const CouponsTab: React.FC<{ campaignId: string; campaignName: string }> = ({
  campaignId,
  campaignName,
}) => {
  const notification = useNotification();
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 50 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, isLoading } = useCoupons(campaignId, pagination.page + 1, pagination.pageSize);
  const patch = usePatchCoupon();

  const download = async () => {
    try {
      const csv = await exportCouponsCsv(campaignId);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${campaignName.replace(/\W+/g, '_')}_coupons.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      notification.error(errorText(err));
    }
  };

  const columns: GridColDef<Coupon>[] = [
    {
      field: 'code',
      headerName: 'Code',
      flex: 1,
      minWidth: 220,
      renderCell: (p) => (
        <span className="flex items-center gap-2 font-mono">
          {p.row.code}
          <button
            type="button"
            aria-label="Copy code"
            onClick={() =>
              void navigator.clipboard
                .writeText(p.row.code)
                .then(() => notification.success('Copied'))
            }
          >
            <Copy size={14} />
          </button>
        </span>
      ),
    },
    {
      field: 'usage',
      headerName: 'Used / max',
      width: 120,
      sortable: false,
      valueGetter: (_v, row) => `${row.usedCount} / ${row.maxUsage ?? '∞'}`,
    },
    {
      field: 'restricted',
      headerName: 'Restricted',
      width: 110,
      sortable: false,
      valueGetter: (_v, row) => (row.restrictedUserId ? 'User' : 'Public'),
    },
    {
      field: 'expiresAt',
      headerName: 'Expires',
      width: 180,
      valueGetter: (_v, row) => formatDateTime(row.expiresAt),
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 90,
      sortable: false,
      renderCell: (p) => (
        <Switch
          size="small"
          checked={p.row.isActive}
          disabled={patch.isPending}
          onChange={(e) =>
            patch.mutate(
              { publicId: p.row.publicId, input: { isActive: e.target.checked } },
              { onError: (err) => notification.error(errorText(err)) }
            )
          }
        />
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap justify-end gap-2">
        <Button startIcon={<Download size={16} />} onClick={() => void download()}>
          Export CSV
        </Button>
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => setDialogOpen(true)}
        >
          Add coupons
        </Button>
      </div>
      <DataGrid
        autoHeight
        rows={data?.items ?? []}
        getRowId={(row) => row.publicId}
        columns={columns}
        loading={isLoading}
        rowCount={data?.total ?? 0}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: 'No coupons yet. Add a code or generate a batch.' }}
      />
      <CreateCouponDialog
        campaignId={campaignId}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  );
};
